import { useEffect, useState } from 'react';
import { useMutation } from '@apollo/client/react';
import { createFilterOptions } from '@mui/material';
import * as R from 'ramda';

import { RELATED_FIELD_AUTOCOMPLETE } from '@atom/graph/task';
import { Modal, SelectAutocomplete } from '@atom/mui';
import {
  RelatedField,
  RelatedFieldComponent,
  RelatedInputData,
} from '@atom/types/task';

import { convertRelatedInputsToTaskValue } from './TaskRelatedFieldGroup';

interface Props {
  open: boolean;
  onClose: () => void;
  onChange: (newFieldValue: RelatedInputData) => void;
  workOrderId: string;
  fieldId: string;
  taskId: string;
  fieldValues: RelatedField[];
}

interface RelatedComponentsMap {
  [title: string]: RelatedFieldComponent;
}

const getFieldsMap = (relatedFields: RelatedField[]): RelatedInputData => {
  return relatedFields.reduce((acc, field) => {
    return {
      ...acc,
      [field.title]: field.value,
    };
  }, {});
};

const TaskRelatedFieldModal = ({
  fieldId,
  taskId,
  fieldValues,
  workOrderId,
  open,
  onClose,
  onChange,
}: Props) => {
  const [componentsMap, setComponentsMap] = useState<RelatedComponentsMap>({});
  const [fieldValuesMap, setFieldValuesMap] = useState<RelatedInputData>(
    getFieldsMap(fieldValues),
  );
  const [autocompleteData, { loading }] = useMutation(
    RELATED_FIELD_AUTOCOMPLETE,
  );

  useEffect(() => {
    const getAutocompleteData = async () => {
      const res = await autocompleteData({
        variables: {
          input: {
            workOrderId,
            taskId,
            fieldId,
            data: convertRelatedInputsToTaskValue(
              R.reject(R.isNil, fieldValuesMap),
            ) as RelatedField[],
          },
        },
      });
      const updatedComponents = R.pathOr(
        [],
        ['data', 'relatedFieldDataAutocomplete', 'components'],
        res,
      );
      const updatedComponentsMap = R.indexBy(
        R.prop('title'),
        updatedComponents,
      );
      setComponentsMap(updatedComponentsMap);
    };

    if (open) {
      getAutocompleteData();
    }
  }, [open, fieldValuesMap]);

  useEffect(() => {
    if (open) {
      setFieldValuesMap(getFieldsMap(fieldValues));
    }
  }, [open]);

  const displayInputStyle = (idx: number): string => {
    if (idx === 0) {
      return 'block';
    }
    const prevComponent = fieldValues[idx - 1];
    return fieldValuesMap[prevComponent.title] ? 'block' : 'none';
  };

  const updateValue = (title, val, idx) => {
    // Changing a value clears the fields after it
    const clearFields: RelatedInputData = fieldValues
      .slice(idx + 1)
      .reduce((acc, component) => {
        return {
          ...acc,
          [component.title]: null,
        };
      }, {});

    setFieldValuesMap({
      ...fieldValuesMap,
      ...clearFields,
      [title]: val,
    });
  };

  const onConfirm = () => {
    onChange(fieldValuesMap);
  };

  const filterOptions = createFilterOptions<string>({
    matchFrom: 'any',
    limit: 250,
  });

  return (
    <Modal
      onCancel={onClose}
      onConfirm={onConfirm}
      title="Edit Fields"
      open={open}
      confirmButtonText="Save"
      disableCloseOnOutsideClick
    >
      {fieldValues.map((field, idx) => (
        <div key={field.title} style={{ margin: '1rem 0' }}>
          <SelectAutocomplete
            style={{ display: displayInputStyle(idx) }}
            id={field.title}
            label={field.title}
            options={R.pathOr([], [field?.title, 'enumeration'], componentsMap)}
            value={R.pathOr(null, [field?.title], fieldValuesMap)}
            onChange={(_, newValue) => updateValue(field.title, newValue, idx)}
            disabled={loading}
            loading={loading}
            blurOnSelect
            filterOptions={filterOptions}
          />
        </div>
      ))}
    </Modal>
  );
};

export default TaskRelatedFieldModal;
